import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiClient } from './services/apiClient';
import type {
  AddressDetails,
  ICheckoutContext,
  RedirectPaymentContext,
  StripeApmNextActionState,
  SuccessScreenContext,
  ThreeDSSecurityState,
} from './types/context';
import { PaymentMethodEnum, type ExcludeOtherPaymentMethod, ProviderEnum } from './types/common';
import type { DrawerScreen } from './types/screens';
import type { CheckoutData, InitialCardPayload, PaymentKeys, PriceBreakdown, SavedCard } from './types/checkout';
import { useCheckoutData } from './api/useCheckoutData';
import { formatTotalPrice } from './utils/pricing';
import { CheckoutContext } from './context/CheckoutContext';
import { organizePaymentMethods } from './utils/paymentMethods';
import { splitSavedInstruments } from './utils/paypal';
import { useCheckout } from './hooks/useCheckout';
import { useGetBoot } from './hooks/useGetBoot';
// `/pure` so merely importing this module (the router pulls it in on EVERY
// route) doesn't inject Stripe.js — see the note in `utils/stripeLoader.ts`.
// It ships the `loadStripe` value only; types still come from the root entry
// (erased at build time, so they inject nothing).
import { loadStripe } from '@stripe/stripe-js/pure';
import type { Stripe } from '@stripe/stripe-js';
import { usePayment } from './hooks/usePayment';
import { stripeService } from './services/stripe';
import { useCardElementRef } from './hooks/useStripeElements';
import { paymentHandler } from './services/paymentHandler';
import { usePaymentToServer } from './hooks/usePaymentToServer';
import { usePciPaymentToServer } from './hooks/usePciPaymentToServer';
import { pciApiClient } from './services/pciApiClient';
import { SESSION_ID } from './constants/params';
import { getDeviceType, isLandscape, isMobileDevice } from './utils/device';
import { STRIPE_CONFIG } from './constants/stripe';
import { useApplicationState } from './hooks/useApplicationState';
import { useOrderPolling } from './hooks/useOrderPolling';
import { PollingStatus, ChargeStates } from './types/order';
import { useInitilizeAdyen } from './hooks/useInitilizeAdyen';
import type { AppchargeErrorType, BasePaymentResponse, PayFromExpressCheckout } from './types/payment';
import { useCreateSession } from './api/session';
import { usePaymentResults } from './hooks/usePaymentResults';
import {
  notifyCheckoutOpened,
  notifyOrderCreated,
  notifyPaymentIntentSuccess,
  notifyCloseCheckout,
  notifyOrderSuccess,
  notifyOrderFailed,
  notifyPaymentIntentFailed,
} from './utils/notifier';
import { forterService } from './services/forterService';
import { getForterToken } from './utils/forter';
import { enrichFinalizeParams } from './utils/paymentParams';
import { StripePaymentHandlerPage } from './pages/StripePaymentHandlerPage';
import { getSessionIdFromUrlParams, isApplePayTabMode, shouldRenderPaymentHandlerPage } from './utils/location';
import { handOffApplePayTabPaymentToOpener } from './utils/applePayTabFallback';
import { useApplePayTabBootstrap } from './hooks/useApplePayTabBootstrap';
import { getRedirectLinkFromUrlParams } from './utils/params';
import { sanitizeMerchantRedirectUrl } from './utils/merchantRedirectUrl';
import { getStripePaymentKey } from './utils/stripe';
import { didSessionStartWithoutValidTax } from './utils/tax';
import {
  getRequestedTemplateType,
  initializeTemplateFromBootConfig,
  shouldFailOnPreRenderError,
} from './utils/templateInitializer';
import { TemplateRenderer } from './templates/TemplateRenderer';
import { getCheckoutTemplate } from './templates/TemplateRegistry';
import { initCoralogixRum, logError, logInfo, setCoralogixLabels, updateRumCustomerData } from './utils/logger';
import { logTabRestoreIfDiscarded } from './utils/paymentDiagnostics';
import { frontendEventsService } from './services/frontendEventsService';
import { initI18n } from './i18n';
import { Maintenance } from './components/Maintenance/Maintenance';
import { StripeApmNextActionForm } from './components/StripeNextAction/StripeApmNextActionForm';
import { TemplateType } from './types/boot';
import { getEventDataForCloseCheckout, getEventDataForSdkEvents } from './utils/eventDataBuilder';
import { useTranslate } from './hooks/useTranslate';
import {
  checkIfServerFinalizeFailure,
  getApiErrorCode,
  getAppchargeErrorMessage,
  getRawApiErrorMessage,
  isVelocityDecline,
} from './utils/error';
import type { ApiError } from './services/apiClient';
import { getTabUrlPreparedForRedirect, handleOpenTab, isRequireToOpenTab } from './utils/redirectPayment';
import { ErrorLogs, InfoLogs } from './constants/logs';
import { getAllowDefaultCheckSavedCardBox } from './utils/useAllowDefaultCheckSavedCardBox';
import CheckoutSkeletonLoader from './components/CheckoutSkeletonLoader/CheckoutSkeletonLoader';
import { useErrorBoundary } from 'react-error-boundary';
import { trackCheckoutOpened } from './utils/checkoutMixpanelTracking';
import { initMixpanel } from './utils/mixpanelInit';
import { promoCodeStateFromApplied, toAppliedPromoCodeData, type PromoCodeState } from './utils/promoCode';
import type { Template } from './types/template';
import { isAppchargeNewCardPayment } from '@/utils/appchargePaymentElement';
import { notifyPaymentIntentClick, trackAndNotifyPaymentClick } from './utils/paymentClickEvents';
import type { PaymentAttemptParams } from './types/context';
import { getTemplateConfig } from './constants/templateConfig';
import { deferIdle } from './utils/defer';
import { getBoot, hasBootParam } from './utils/boot';
import { FeatureFlag } from './constants/featureFlags';
import { useResumePendingChallenge } from './hooks/useResumePendingChallenge';
import { useDisablePullToRefresh } from './hooks/useDisablePullToRefresh';
import { useAccessibilityWidget } from './hooks/useAccessibilityWidget';
import { clearPendingChallenge } from './utils/pendingChallengeStore';
import { prewarmFallbackTemplatePayments } from './utils/preloadResources';
import { shouldCloseCheckoutOnHiddenSuccessScreen } from './utils/conditionalRendering';
import { resolveIsRedirectFlow } from './utils/checkoutMode';
import { shouldDeferBootContentEnter } from './utils/checkoutTransitions';
import { FrontendEventReason } from './constants/frontendEvents';
import { CheckoutSessionErrorCode } from './constants/checkoutSessionErrorCodes';
import { isInlineScreenSurface } from './utils/screenSurface';

export const CheckoutContent = () => {
  const { showBoundary } = useErrorBoundary();
  const [searchParams] = useSearchParams();
  const sessionId = getSessionIdFromUrlParams();
  const bootData = useGetBoot();
  // ACDEV-29368: publisher opt-in kill switch for browser pull-to-refresh
  // when the checkout owns the top-level browsing context. See the hook for
  // scope details. No-op inside an iframe and when the FF is OFF/absent.
  useDisablePullToRefresh(Boolean(bootData.boot?.featureFlags?.[FeatureFlag.CHECKOUT_DISABLE_PULL_TO_REFRESH]));
  // ACDEV-30609: publisher accessibility script with a draggable icon. No-op
  // while the FF is OFF/absent, boot has not resolved a publisher id yet, or
  // the boot asks for EMBEDDED_VIEW (no safe icon frame vs Zip/Pay).
  useAccessibilityWidget({
    publisherId: bootData.boot?.checkoutTheme?.publisherId,
    enabled: Boolean(bootData.boot?.featureFlags?.[FeatureFlag.STORE_ACCESSIBILITY_SCRIPT_SUPPORT]),
    templateType: getRequestedTemplateType(bootData.boot),
  });
  const checkout = useCheckout();
  const payment = usePayment();
  const { cardElementRef, setCardElementRef } = useCardElementRef();
  const [redirectPayment, setRedirectPayment] = useState<RedirectPaymentContext>();
  const openedTabRef = useRef<Window | null>(null);
  const [successScreen, setSuccessScreen] = useState<SuccessScreenContext>();
  const [threeDSSecurityState, setThreeDSSecurityState] = useState<ThreeDSSecurityState>({ show3DSForm: false });
  const [stripeApmAction, setStripeApmAction] = useState<StripeApmNextActionState>({ active: false });
  const [error, setError] = useState<string | null>(null);
  const [showBootSkeletonExit, setShowBootSkeletonExit] = useState(true);
  const paymentToServer = usePaymentToServer();
  const pciPaymentToServer = usePciPaymentToServer();
  const isInitialized = useRef(false);
  const { adyenCardDetailsRef } = useInitilizeAdyen();
  const [isApiInitilized, setIsApiInitilized] = useState(false);
  const [stripeInstance, setStripeInstance] = useState<Stripe | null>(null);
  // iOS <= 16 Apple Pay fallback tab: the checkout data arrives from the
  // opener via postMessage (seeded into the query cache), so the fetch is
  // held off while the handshake is in flight and skipped entirely once
  // seeded — `/checkout/v3` only re-fires as a last resort (`fallback`).
  const applePayTabMode = isApplePayTabMode();
  const {
    status: applePayTabBootstrapStatus,
    promoCode: applePayTabPromoCode,
    addressDetails: applePayTabAddressDetails,
  } = useApplePayTabBootstrap(applePayTabMode);
  const checkoutData = useCheckoutData({
    enableFetch:
      isApiInitilized && !bootData.isLoading && (!applePayTabMode || applePayTabBootstrapStatus === 'fallback'),
  });
  const lastPaymentMethodUsedRef = useRef<PaymentMethodEnum>();
  const paymentAttemptEventDataRef = useRef<PaymentAttemptParams>();
  // ACDEV-27856: synchronous guard against a second concurrent `runPayment`
  // call (e.g. a rapid double-click on the card Pay button before React
  // re-renders `isInPaymentProcess`). A ref is required — React state alone
  // can't block a second call that fires before the first state update is
  // committed. Released everywhere the in-flight payment ends: the `catch`
  // in `runPayment`, `stopPaymentProcess`, and the polling-SUCCESS effect.
  const isRunPaymentInProgressRef = useRef(false);
  const { mutateAsync: createSession } = useCreateSession();
  const [showMaintenance, setShowMaintenance] = useState(false);
  const { t } = useTranslate();
  const { isDefaultSaveCardAllowedForLocation } = getAllowDefaultCheckSavedCardBox({
    customer: checkout.customer,
    addressDetails: checkout.addressDetails,
    featureFlags: bootData.boot?.featureFlags,
  });

  const {
    triggerOrderPolling,
    statusOrderPolling,
    data: orderData,
    error: orderPollingError,
    errorCode: orderPollingErrorCode,
  } = useOrderPolling();

  // ACDEV-25945 (Option 2): order ids that are created/changed after RUM init
  // (e.g. redirect / express pay flows) won't be in the init-time global labels,
  // so merge them in dynamically. `setCoralogixLabels` no-ops until RUM is
  // initialized, and the value is also set at init, so this only fills the gap
  // for late/changed order ids.
  useEffect(() => {
    if (checkout.orderId) {
      setCoralogixLabels({ order_id: checkout.orderId });
    }
  }, [checkout.orderId]);

  const sendPaymentToServer = useCallback(
    async <TResponse = unknown,>(params: unknown): Promise<TResponse> =>
      (await paymentToServer.mutateAsync(enrichFinalizeParams(params, getForterToken()))) as TResponse,
    [paymentToServer]
  );

  const sendPciPaymentToServer = useCallback(
    async <TResponse = unknown,>(params: unknown): Promise<TResponse> =>
      (await pciPaymentToServer.mutateAsync(enrichFinalizeParams(params, getForterToken()))) as TResponse,
    [pciPaymentToServer]
  );

  const closeDrawer = useCallback(() => {
    if (isInlineScreenSurface(checkout.template?.type)) {
      checkout.setViewer(undefined);
    } else {
      checkout.setDrawerScreen(undefined);
    }
  }, [checkout]);

  /**
   * True from the moment a discarded challenge is resumed until it settles.
   *
   * `clearState` runs on every `paymentMethodSelected` change, and boot assigns
   * the template's default method in the same pass that publishes the payment
   * methods the resume waits for. So the reset lands immediately after the
   * resume, closing the challenge before the shopper ever sees it — and because
   * a challenge is only resumed once per page, it is never reopened. Boot-time
   * churn must not cancel a payment that is already in flight.
   */
  const isResumedChallengeOpenRef = useRef(false);

  /**
   * Mirrors the 3DS form's visibility for the method-change effect below,
   * which must not tear down a challenge that is still waiting on the issuer.
   * A mirror rather than a dependency: as a dependency it would re-run that
   * effect the moment the form closes on settle, and the reset it performs
   * wipes the success screen the settle handler has just put up.
   */
  const openChallengeRef = useRef<{ isOpen: boolean; provider?: ProviderEnum }>({ isOpen: false });
  openChallengeRef.current = { isOpen: threeDSSecurityState.show3DSForm, provider: threeDSSecurityState.provider };

  const clearState = useCallback(() => {
    if (isResumedChallengeOpenRef.current) return;
    setError(null);
    setSuccessScreen(undefined);
    payment.setIsExpressPaymentInProcess(false);
    setThreeDSSecurityState({ show3DSForm: false });
    setStripeApmAction({ active: false });
    payment.setIsInPaymentProcess(false);
    isRunPaymentInProgressRef.current = false;
  }, []);

  const saveNewCard = useCallback(
    (card: SavedCard) => {
      checkout.setSavedCards([...checkout.savedCards, { ...card, isNew: true }]);
      checkout.setSelectedCard({ ...card, isNew: true });
    },
    [checkout]
  );

  const handlePaymentError = useCallback(
    (appChargeErrorType?: AppchargeErrorType, provider?: ProviderEnum, rawReason?: string) => {
      // The decline reason travels in `additionalData` (3rd arg), not the
      // `Error` slot (2nd): `logError` stringifies a non-Error there, so a
      // plain object lands in RUM as "[object Object]" with its fields gone.
      const failureContext = {
        appChargeErrorType: appChargeErrorType ?? 'none',
        provider: provider ?? 'none',
        rawReason: rawReason ?? 'none',
        paymentMethod: lastPaymentMethodUsedRef.current ?? 'none',
      };
      logError(ErrorLogs.checkoutPaymentError, undefined, failureContext);
      // Velocity/rate-limit declines are recoverable (wait & retry or switch
      // method) rather than a hard card failure — emit a dedicated RUM signal
      // so a spike is trackable on its own and not buried in generic declines.
      if (isVelocityDecline(appChargeErrorType)) {
        logError(ErrorLogs.velocityDecline, undefined, failureContext);
      }
      // Surface the actual decline reason (e.g. "insufficient funds",
      // "expired card"). When the failure payload carries no specific
      // type, `getAppchargeErrorMessage` falls back to GENERIC_DECLINE
      // ("Payment could not be processed…") rather than the unhelpful
      // generic "Something went wrong" — same copy the redirect path uses.
      // This localized copy is for the screen only; the SDK event carries the
      // raw PSP text (`rawReason`) or no reason at all.
      setError(getAppchargeErrorMessage(t, appChargeErrorType));
      // Dismiss any in-flight next-action (card/wallet 3DS or generic-APM) so
      // the shopper returns to a usable form (with the error surfaced) rather
      // than a stuck/hidden-loader state.
      setThreeDSSecurityState({ show3DSForm: false });
      setStripeApmAction({ active: false });
      // The challenge's own exit already clears this, but the form can be
      // dismissed by paths that never reach it. A finished payment must not
      // leave anything a later boot could resume.
      clearPendingChallenge();
      // Settled, so ordinary method-switch resets apply again.
      isResumedChallengeOpenRef.current = false;
      const attemptEventData = paymentAttemptEventDataRef.current;
      notifyPaymentIntentFailed(checkoutData.data!, lastPaymentMethodUsedRef.current as PaymentMethodEnum, {
        reason: rawReason,
        promoCode: attemptEventData?.promoCode,
        isSavedPaymentMethodUsed: attemptEventData?.isSavedPaymentMethodUsed,
        isNewPaymentMethodSaved: attemptEventData?.isNewPaymentMethodSaved,
      });
      if (provider && isRequireToOpenTab(provider, lastPaymentMethodUsedRef.current as PaymentMethodEnum)) {
        closeRedirectTab();
      }
    },
    [t, checkoutData.data!]
  );

  const handlePaymentSuccess = useCallback(
    (result: BasePaymentResponse, provider: ProviderEnum) => {
      const resultStripe = (result as { stripe?: Record<string, unknown> }).stripe;
      logInfo(InfoLogs.checkoutPaymentSuccess, {
        result: resultStripe ? { ...result, stripe: { ...resultStripe, clientSecret: undefined } } : result,
        provider,
      });
      // The next action (card/wallet 3DS or generic-APM approval) has resolved
      // by the time we reach success. Tear both channels down so the driver
      // won't re-run and the processing loader takes over the final finalize +
      // order-polling stage instead of leaving the bare checkout.
      setThreeDSSecurityState({ show3DSForm: false });
      setStripeApmAction({ active: false });
      // See the matching call in `handlePaymentError`: a paid order must never
      // leave a resumable challenge behind.
      clearPendingChallenge();
      isResumedChallengeOpenRef.current = false;
      // Apple Pay fallback tab: this window only hosted the payment sheet.
      // Everything below — polling, success screen, SDK events — belongs to
      // the framed checkout, so hand the result back and close.
      if (handOffApplePayTabPaymentToOpener(result, provider)) return;
      triggerOrderPolling(result.orderId);
      setSuccessScreen({
        isOpen: true,
        paymentDetails: { params: result },
        provider,
      });
      const attemptEventData = paymentAttemptEventDataRef.current;
      notifyPaymentIntentSuccess(
        getEventDataForSdkEvents(checkoutData.data!, {
          paymentMethodName: lastPaymentMethodUsedRef.current,
          promoCode: attemptEventData?.promoCode ?? null,
          isSavedPaymentMethodUsed: attemptEventData?.isSavedPaymentMethodUsed,
          isNewPaymentMethodSaved: attemptEventData?.isNewPaymentMethodSaved,
        })
      );
    },
    [triggerOrderPolling, checkoutData.data]
  );

  const handlePaymentPending = useCallback(
    (result: BasePaymentResponse, provider: ProviderEnum) => {
      logInfo('Payment pending; waiting for order resolution', {
        orderId: result.orderId,
        provider,
      });
      setThreeDSSecurityState({ show3DSForm: false });
      setStripeApmAction({ active: false });
      if (handOffApplePayTabPaymentToOpener(result, provider)) return;
      triggerOrderPolling(result.orderId);
      setSuccessScreen({
        isOpen: true,
        paymentDetails: { params: result },
        provider,
      });
      // Match handlePaymentSuccess — host SDK listeners expect payment-intent
      // success when UI already shows the success screen + order polling.
      const attemptEventData = paymentAttemptEventDataRef.current;
      notifyPaymentIntentSuccess(
        getEventDataForSdkEvents(checkoutData.data!, {
          paymentMethodName: lastPaymentMethodUsedRef.current,
          promoCode: attemptEventData?.promoCode ?? null,
          isSavedPaymentMethodUsed: attemptEventData?.isSavedPaymentMethodUsed,
          isNewPaymentMethodSaved: attemptEventData?.isNewPaymentMethodSaved,
        })
      );
    },
    [triggerOrderPolling, checkoutData.data]
  );

  /**
   * Mirror image of `handOffApplePayTabPaymentToOpener`, on the receiving
   * side: the fallback tab authorized an Apple Pay payment and closed itself,
   * so this frame resumes the flow as if the payment had happened here —
   * order polling, success screen and SDK events all run in the window the
   * publisher's SDK is attached to.
   */
  const resumeApplePayTabPayment = useCallback(
    (result: BasePaymentResponse, provider: ProviderEnum) => {
      // The tab never ran a payment in this window, so the attribution the
      // SDK events read is still empty — the fallback only ever pays with
      // Apple Pay, so name it before the events below fire.
      lastPaymentMethodUsedRef.current = PaymentMethodEnum.APPLE_PAY;
      // Swap the "finish in the new window" overlay for the regular
      // processing spinner; the polling-SUCCESS effect clears it.
      payment.setIsApplePayTabOpen(false);
      payment.setIsExpressPaymentInProcess(true);
      handlePaymentSuccess(result, provider);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `payment` setters are stable
    [handlePaymentSuccess]
  );

  const { handlePaymentResults } = usePaymentResults({
    paymentMethods: checkout.paymentMethods,
    onSuccess: handlePaymentSuccess,
    onPending: handlePaymentPending,
    onError: handlePaymentError,
    onRedirect: (params: RedirectPaymentContext) => setRedirectPayment((prev) => ({ ...prev, ...params })),
    on3DSRequired: setThreeDSSecurityState,
    onStripeApmAction: setStripeApmAction,
    actions: {
      stopPaymentProcess: () => {
        payment.setIsInPaymentProcess(false);
        payment.setIsExpressPaymentInProcess(false);
        isRunPaymentInProgressRef.current = false;
      },
      // Deferred reference: `closeRedirectTab` is declared below this hook call
      // (const arrow, not hoisted), so wrap it to read it lazily at call time.
      closeRedirectTab: () => closeRedirectTab(),
    },
  });

  // A challenge whose page was discarded mid-authentication left a record
  // behind; re-show it so the form's own watchdog can settle it.
  useResumePendingChallenge({
    isEnabled: Boolean(bootData.boot?.featureFlags?.[FeatureFlag.ADYEN_3DS_CHALLENGE_RESUME]),
    isCheckoutReady: Boolean(checkout.paymentMethods?.length),
    on3DSRequired: setThreeDSSecurityState,
    onSuccess: handlePaymentSuccess,
    onError: handlePaymentError,
    stopPaymentProcess: () => {
      payment.setIsInPaymentProcess(false);
      payment.setIsExpressPaymentInProcess(false);
      isRunPaymentInProgressRef.current = false;
    },
    onResumed: (activeMethod) => {
      // All of this died with the page. The method is stamped onto the SDK
      // events publishers consume, and the in-flight flag is what stops the
      // checkout being interactive once the challenge settles and the order is
      // still resolving — `runPayment` sets it before finalize on the live path.
      lastPaymentMethodUsedRef.current = activeMethod;
      isResumedChallengeOpenRef.current = true;
      payment.setIsInPaymentProcess(true);
    },
  });

  const initilizeCheckout = () => {
    const checkoutSessionId = searchParams.get(SESSION_ID) || sessionId;
    if (!checkoutSessionId || isApiInitilized) return;

    apiClient.initilize({ token: checkoutSessionId });
    pciApiClient.initilize({ token: checkoutSessionId });
    // Authenticated events-gateway client (`Authorization: checkout <token>`).
    // Publisher id is stamped once boot resolves in `initilizeCheckoutData`.
    frontendEventsService.initialize({ sessionToken: checkoutSessionId });

    /**
     * Perf (C.1): defer analytics SDK init off the critical path.
     * - Forter is a fraud SDK: its script sets the Forter cookie and
     *   needs maximum dwell time on the page to fingerprint the device
     *   before pay-time, so it must load as early as possible. Deferring
     *   it until interaction caused sessions (fast/express checkouts,
     *   no-interaction pays) to ship with no Forter cookie/token, which
     *   Forter flags as "cookie not being sent". Init eagerly — the
     *   script tag is `async` + `fetchpriority="low"`, so it still yields
     *   to Stripe/Adyen/`/checkout/v3` on the network.
     * - Mixpanel runs internal state setup; events fired before init are
     *   buffered by `mixpanelService` and replayed on drain, so we don't
     *   lose any tracking from the first-paint window.
     *
     * `trackCheckoutOpened` below is safe to run before init because
     * `mixpanelService.track` queues until init resolves.
     */
    forterService.initialize();
    initMixpanel(FeatureFlag.ENABLE_MIXPANEL_SESSION_RECORDING);
    // `publisher_id` is not on the context yet; read it off the boot. Absent
    // or invalid boot → `undefined`, the event still fires.
    const bootPublisherId: Promise<string | undefined> = hasBootParam()
      ? getBoot()
          .then((boot) => boot?.checkoutTheme?.publisherId)
          .catch(() => undefined)
      : Promise.resolve(undefined);
    void bootPublisherId.then((publisherId) => trackCheckoutOpened(checkoutSessionId, checkoutContext, publisherId));

    initI18n(checkoutSessionId).catch((error) => {
      logError('Failed to initialize i18n', error);
    });

    setIsApiInitilized(true);
  };

  const openRedirectTab = () => {
    const openedTab = handleOpenTab(getTabUrlPreparedForRedirect());
    openedTabRef.current = openedTab;
    setRedirectPayment({ openedTab });
  };

  const closeRedirectTab = () => {
    if (openedTabRef.current) {
      openedTabRef.current.close();
      openedTabRef.current = null;
      setRedirectPayment(undefined);
    }
  };
  useEffect(() => {
    // A challenge in flight outranks a method change. `clearState` hides the
    // 3DS form, and hiding it unmounts the component that owns the Adyen
    // challenge, the order poll and the liveness heartbeat — so the payment
    // loses every path it had left to report an outcome, with no error and,
    // until recently, no log. It is the shape of the whole silent cohort:
    // page alive, no lifecycle event, never backgrounded, heartbeat stopping
    // inside 30s, order still `payment_pending`.
    //
    // Nothing is lost by skipping it. Both settle handlers close the form and
    // reset this same state, and `runPayment` calls `clearState` directly
    // before starting the next attempt.
    // Read through a ref deliberately. As a dependency it would re-run this
    // effect when the form closes on settle, and `clearState` wipes the
    // success screen that the settle handler has just set.
    if (openChallengeRef.current.isOpen) {
      // One challenge that would previously have been destroyed. Counting them
      // is the only way to show the fix did something, since a fix that works
      // otherwise shows up purely as the absence of teardowns.
      //
      // `provider` is carried because the reset was never Adyen-specific: the
      // first one this caught in production was a Forter/Stripe challenge, and
      // counting those as Adyen would inflate the very number the Adyen fix is
      // being judged on.
      logInfo(InfoLogs.threeDSChallengeKeptThroughReset, {
        provider: openChallengeRef.current.provider,
        paymentMethodSelected: checkout.paymentMethodSelected,
        otherMethodSelected: checkout.otherMethodSelected,
      });
      return;
    }
    clearState();
  }, [checkout.paymentMethodSelected, checkout.otherMethodSelected, clearState]);

  useEffect(() => {
    initilizeCheckout();
  }, [searchParams, sessionId, isApiInitilized]);

  const failCheckoutOpen = useCallback(
    (errorSource: unknown) => {
      showBoundary('unableToLoadCheckout');
      // Zero methods / a rejected `/checkout/v3` never reaches a mounted form,
      // so `setError` would paint nowhere under the boot skeleton. The error
      // boundary is the only surface that replaces it.
      notifyPaymentIntentFailed(checkoutData.data ?? ({} as CheckoutData), undefined, {
        reason: FrontendEventReason.UNABLE_TO_LOAD_CHECKOUT,
        error: {
          reason: FrontendEventReason.UNABLE_TO_LOAD_CHECKOUT,
          code: getApiErrorCode(errorSource),
          isFatal: true,
        },
      });
    },
    [checkoutData.data, showBoundary]
  );

  const initilizeCheckoutData = useCallback(() => {
    try {
      if (!checkoutData.data || !bootData.boot || isInitialized.current || !isApiInitilized) return;

      // Must land before CHECKOUT_OPENED *and* before failCheckoutOpen: the FE
      // events service buffers until publisher_id is set, and showBoundary
      // unmounts this tree so a later setPublisherId never runs.
      frontendEventsService.setPublisherId(bootData.boot.publisherData.id);

      // `/checkout/v3` rejected: `useCheckoutData` still exposes `{}` so this
      // gate passes. Bail to the error screen before template validatePreRender
      // can `setError` and leave the boot skeleton up forever
      // (MissingPaymentMethodsError is the production case for SINGLE).
      if (checkoutData.isError) {
        failCheckoutOpen(checkoutData.error);
        return;
      }

      // Apple Pay fallback tab: CHECKOUT_OPENED and ORDER_CREATED are
      // session-scoped, and the opener already emitted both for this session.
      // The tab's messenger points at the same publisher page, so emitting
      // them again would have the SDK see one checkout open twice.
      if (!applePayTabMode) {
        notifyCheckoutOpened(getEventDataForSdkEvents(checkoutData.data));
      }

      // A vaulted PayPal account arrives inside `savedCards[]` (BFF tags it
      // `paymentProvider === 'paypal'`). Split it out so it never renders as a
      // saved CARD — instead it drives the one-click PayPal CTA.
      const { cards: savedCardsToUse, paypalAccount: savedPaypalAccount } = splitSavedInstruments(
        checkoutData.data.savedCards
      );

      if (savedCardsToUse.length) {
        checkout.setSelectedCard(savedCardsToUse[0]);
      }

      const checkoutConfig = checkoutData.data.checkoutConfigurations;
      // Resolve the active template FROM THE BOOT first — that is the
      // only source the BE actually populates with `template` and
      // `availablePaymentMethods` (validated 2026-05-21 against staging).
      // The previous code resolved from `checkoutData.checkoutConfigurations.template`
      // which is never echoed, so `requestedTemplate` was always DEFAULT
      // and SinglePaymentTemplate.validatePreRender / filterAvailablePaymentMethods
      // never ran in production.
      //
      // Apple Pay fallback tab: force the SINGLE layout (details on top,
      // one method below — the mock for this flow) with Apple Pay as the
      // ONLY method, regardless of what the boot template says. The
      // template's boot-allow-list validation/filter is skipped: the
      // allow-list belongs to the publisher's own SINGLE config, not to
      // this synthetic one-method view.
      const template = applePayTabMode
        ? getTemplateConfig(TemplateType.SINGLE_PAYMENT_METHOD)
        : initializeTemplateFromBootConfig(bootData.boot!, checkoutData.data!);
      const activeTemplate = getCheckoutTemplate(template.type);
      const initContext = {
        isMobile: isMobileDevice(),
        isLandscape: isLandscape(),
        device: getDeviceType(),
        bootData: bootData.boot,
        checkoutData: checkoutData.data,
        paymentMethods: checkoutData.data.paymentMethods,
      };

      const preRenderError = applePayTabMode ? null : activeTemplate.validatePreRender(initContext);
      if (preRenderError) {
        logError(ErrorLogs.checkoutPaymentMethodNotAvailable, undefined, { reason: t(preRenderError) });
        if (shouldFailOnPreRenderError(template.type)) {
          failCheckoutOpen(checkoutData.error);
          return;
        }
      }

      // The template's own filter already applies the boot's allow-list
      // (see SinglePaymentTemplate.filterAvailablePaymentMethods); a
      // duplicate `filterByConfiguredAvailablePaymentMethods` call against
      // `checkoutConfigurations` here was a no-op (plural source is never
      // populated) — removed.
      const templateFiltered = applePayTabMode
        ? checkoutData.data.paymentMethods.filter((method) => method.paymentMethodName === PaymentMethodEnum.APPLE_PAY)
        : activeTemplate.filterAvailablePaymentMethods(checkoutData.data.paymentMethods, initContext);
      const paymentMethodsToUse = templateFiltered;
      const paymentMethodsAvailableToUse = [...templateFiltered];

      const organizedPaymentMethods = organizePaymentMethods(paymentMethodsAvailableToUse, checkoutConfig);
      if (organizedPaymentMethods?.length === 0) {
        // Zero methods is also what an unusable session looks like from here:
        // a failed `/checkout/v3` leaves `.data` empty, so the query error is
        // what says whether the session ended rather than the template filtering
        // everything out.
        failCheckoutOpen(checkoutData.error);
        return;
      }
      checkout.setTemplate(template);
      checkout.setPaymentMethodsAvailable(organizedPaymentMethods);
      checkout.setPurchasedItems(checkoutData.data.items);
      checkout.setCustomer(checkoutData.data.customer);
      checkout.setPaymentMethods(paymentMethodsToUse);

      // Perf: when on the single-CTA "preferred method" templates (FTD
      // / last-method, including the embedded variant), the user may at
      // any point fall back to the full picker — either implicitly via
      // `useExpressWalletFallback` (wallet unavailable) or explicitly via
      // the "Use other payment method" link. Prewarm the PSP instances
      // that picker will need (Adyen card + every Stripe sub-mid pk) at
      // idle, so the fallback render is already fully warm. No-op for
      // DEFAULT/SinglePay/EMBEDDED_VIEW (their active form already loads
      // those PSPs).
      if (template.features.mobileOnlyExpressFirstLayout) {
        prewarmFallbackTemplatePayments({
          paymentMethods: paymentMethodsToUse,
          customer: checkoutData.data.customer,
        });
      }
      checkout.setPaymentKeys(checkoutData.data.pks ?? ({} as PaymentKeys));
      checkout.setPublisher(bootData.boot?.publisherData);
      checkout.setPricing(checkoutData.data.pricing);
      // ACDEV-27914: latch the "session hydrated without a resolved US/CA
      // tax" bit ONCE, before any mid-session /calculate-estimated-tax (e.g.
      // wallet-returned ZIP → `applyWalletTaxToCheckout`) can flip
      // `isTaxValid` to true. The whole wallet-derived ZIP + tax path
      // (`shouldUseWalletDerivedZipTax`) stays active for the rest of the
      // session for these buyers even when `WALLET_ZIP_TAX` is OFF.
      checkout.setSessionStartedWithoutValidTax(
        didSessionStartWithoutValidTax(checkoutData.data.customer, checkoutData.data.pricing)
      );
      // Apple Pay fallback tab: the pricing above already carries the
      // opener's live breakdown (promo/ZIP tax merged into the handed-off
      // payload); mirror the applied promo itself so analytics events match.
      if (applePayTabMode && applePayTabPromoCode) {
        checkout.updatePromoCodeBreakdown(promoCodeStateFromApplied(applePayTabPromoCode));
      }
      checkout.setSavedCards(savedCardsToUse);
      checkout.setSavedPaypalAccount(savedPaypalAccount);
      checkout.setCheckoutConfigurations(checkoutConfig);
      checkout.setRedirectLink(
        sanitizeMerchantRedirectUrl(checkoutData.data.redirectUrl) ??
          sanitizeMerchantRedirectUrl(getRedirectLinkFromUrlParams()) ??
          ''
      );
      checkout.setOffer(checkoutData.data.offer);
      checkout.setOffers(checkoutData.data.offers ?? []);
      checkout.setOrderId(checkoutData.data.orderId);
      // Apple Pay fallback tab: an address edited in the opener never reaches
      // the cached `/checkout/v3` customer, so prefer what the handoff carried
      // — otherwise the tab shows the pre-edit ZIP beside the post-edit tax.
      checkout.setAddressDetails(
        applePayTabAddressDetails ?? {
          address: checkoutData.data.customer?.address?.address ?? '',
          zipCode: checkoutData.data.customer?.address?.zipCode ?? '',
        }
      );

      // PR #210 extracted this into `getDefaultPaymentMethodSelected(methods, template)`
      // — a single utility hard-coding "Apple Pay on iOS / Google Pay on
      // Android for PREFERRED, first non-express otherwise". Our template-class
      // architecture is a superset: every template owns its own resolver
      // (`PreferredPaymentTemplate.resolveDefaultPaymentMethod` returns the
      // device's express wallet OR the BE-supplied last method,
      // `DefaultTemplate` returns first non-express, etc.). New templates
      // pick up the slot without editing this file.
      const defaultMethod = activeTemplate.resolveDefaultPaymentMethod({
        device: getDeviceType(),
        paymentMethods: organizedPaymentMethods,
        bootData: bootData.boot,
        checkoutData: checkoutData.data,
      });
      if (defaultMethod) {
        checkout.setPaymentMethodSelected(defaultMethod);
      }

      // Update Coralogix RUM with customer data when available
      const checkoutSessionId = searchParams.get(SESSION_ID) || sessionId;
      if (checkoutSessionId && checkoutData.data.customer?.email) {
        updateRumCustomerData(checkoutSessionId, checkoutData.data.customer.email);
      }
      /**
       * Perf (C.1): Coralogix RUM init bootstraps a session and registers
       * fetch/XHR/error hooks. Defer to idle so it doesn't compete for the
       * main thread with the first paint of the interactive checkout. Logs
       * emitted before init are queued and replayed on init.
       *
       * Read into locals first: the guard at the top of this function narrows
       * `bootData.boot` to non-null, but TS discards property narrowing inside
       * the deferred closure, so inlining it fails `BootData | null`.
       */
      const bootForRum = bootData.boot;
      const emailForRum = checkoutData.data.customer?.email;
      const orderIdForRum = checkoutData.data.orderId;
      deferIdle(() => {
        initCoralogixRum(emailForRum, bootForRum, orderIdForRum);
        // Must follow init to be shippable, and reports on the document that
        // this one replaced — the only evidence of a browser-initiated kill
        // that outlives the killed page (ACDEV-26410).
        logTabRestoreIfDiscarded();
      });
      if (!applePayTabMode) {
        notifyOrderCreated(getEventDataForSdkEvents(checkoutData.data!));
      }
      checkout.setDataIsReady(true);
      isInitialized.current = true;
    } catch (error) {
      // A gateway failure lands on the query, not in this `catch` — init runs
      // against the empty payload and throws locally. The query error is the
      // one carrying `errorCode`.
      failCheckoutOpen(checkoutData.error ?? error);
    }
    // `t` deliberately omitted: i18n identity thrashing used to re-fire init.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    checkoutData.data,
    checkoutData.error,
    checkoutData.isError,
    bootData.boot,
    checkout,
    isApiInitilized,
    searchParams,
    sessionId,
    applePayTabMode,
    applePayTabPromoCode,
    applePayTabAddressDetails,
    failCheckoutOpen,
  ]);

  const closeCheckout = useCallback((context: ICheckoutContext, reason?: string) => {
    // Apple Pay fallback tab: this window is not the checkout the publisher
    // SDK embedded, so CLOSE_CHECKOUT is left to the opener (which emits it
    // once the tab closes). Here, closing means closing the tab and handing
    // the shopper back to the publisher page.
    if (isApplePayTabMode()) {
      window.close();
      return;
    }
    const attemptEventData = paymentAttemptEventDataRef.current;
    notifyCloseCheckout(
      getEventDataForCloseCheckout(context!, {
        error: { reason },
        isSavedPaymentMethodUsed: attemptEventData?.isSavedPaymentMethodUsed,
        isNewPaymentMethodSaved: attemptEventData?.isNewPaymentMethodSaved,
      })
    );
  }, []);

  const trackPaymentAttempt = useCallback(
    (context: ICheckoutContext, params: unknown, selectedPaymentMethod: PaymentMethodEnum) => {
      const paymentAttemptParams = params as PaymentAttemptParams | undefined;
      const appliedPromoCode =
        paymentAttemptParams?.promoCode ?? toAppliedPromoCodeData(context.promoCodeBreakdown) ?? undefined;
      const paymentAttemptEventData: PaymentAttemptParams = {
        paymentMethodName: paymentAttemptParams?.paymentMethodName ?? selectedPaymentMethod,
        isSavedPaymentMethodUsed: paymentAttemptParams?.isSavedPaymentMethodUsed ?? false,
        isNewPaymentMethodSaved: paymentAttemptParams?.isNewPaymentMethodSaved ?? false,
        promoCode: appliedPromoCode,
      };

      paymentAttemptEventDataRef.current = {
        ...paymentAttemptEventData,
      };

      trackAndNotifyPaymentClick(context, paymentAttemptEventData);
      notifyPaymentIntentClick(context, paymentAttemptEventData);
    },
    [checkout.promoCodeBreakdown]
  );

  const checkoutContext: ICheckoutContext | undefined = useMemo(() => {
    if (!checkout.dataIsReady || !isApiInitilized) return;

    const context: ICheckoutContext = {
      promoCodeBreakdown: checkout.promoCodeBreakdown,
      paymentMethodsAvailable: checkout.paymentMethodsAvailable,
      paymentMethodSelected: checkout.paymentMethodSelected,
      boot: bootData.boot!,
      redirectLink: checkout.redirectLink ?? '',
      showMaintenance: showMaintenance,
      orderId: checkout.orderId,
      sessionId: sessionId,
      actions: {
        updatePromoCodeBreakdown: (update: Partial<PromoCodeState>) =>
          checkout.updatePromoCodeBreakdown((prev) => ({ ...prev, ...update })),
        showMaintenance: () => setShowMaintenance(true),
        setRedirectLink: (redirectLink: string) => checkout.setRedirectLink(redirectLink),
        startOrderPolling: (orderId: string) => triggerOrderPolling(orderId),
        removeCard: (card: SavedCard) => checkout.handleRemoveCard(card),
        closeCheckout,
        createSession,
        // Wrapped like `setIsInPaymentProcess` below: express (Apple Pay /
        // Google Pay) cancel/timeout/error paths across the wallet forms and
        // `useApplePayTabHandoff` release this directly, without going
        // through `runPayment`'s `catch` or `stopPaymentProcess`.
        setIsExpressPaymentInProcess: (isExpressPaymentInProcess = true) => {
          if (!isExpressPaymentInProcess) {
            isRunPaymentInProgressRef.current = false;
          }
          payment.setIsExpressPaymentInProcess(isExpressPaymentInProcess);
        },
        setApplePayTabOpen: (isOpen: boolean) => payment.setIsApplePayTabOpen(isOpen),
        resumeApplePayTabPayment,
        setIsFormValid: payment.setIsFormValid,
        setRedirectPayment: (redirectPayment: RedirectPaymentContext) => setRedirectPayment(redirectPayment),
        openRedirectTab,
        closeRedirectTab,
        updateAddressDetails: (addressDetails: AddressDetails) => checkout.setAddressDetails(addressDetails),
        setCustomerEmail: (email: string) => checkout.setCustomer({ ...checkout.customer!, email }),
        setPaymentMethodSelected: (paymentMethod: PaymentMethodEnum) =>
          checkout.setPaymentMethodSelected(paymentMethod as PaymentMethodEnum),
        updateOtherMethodSelected: (method: ExcludeOtherPaymentMethod) => checkout.setOtherMethodSelected(method),
        // Wrapped (not the raw `payment.setIsInPaymentProcess`): some
        // terminal redirect paths (shopper-cancel, decline, tab-closed — see
        // RedirectPayment/CashAppRedirect) release the in-flight flag
        // directly, without going through `runPayment`'s `catch` or
        // `stopPaymentProcess`. Without this, `isRunPaymentInProgressRef`
        // would stay stuck `true` after those paths, permanently blocking
        // every future `runPayment` call even though the button looks
        // enabled again.
        setIsInPaymentProcess: (isInPaymentProcess: boolean) => {
          if (!isInPaymentProcess) {
            isRunPaymentInProgressRef.current = false;
          }
          payment.setIsInPaymentProcess(isInPaymentProcess);
        },
        setThreeDSSecurityState: (threeDSSecurityState: ThreeDSSecurityState) =>
          setThreeDSSecurityState(threeDSSecurityState),
        runPayment: async <TResponse = unknown,>(
          params?: unknown,
          payFromExpressCheckout?: PayFromExpressCheckout,
          provider?: ProviderEnum
        ): Promise<TResponse | undefined> => {
          // ACDEV-27856: a second call while one is already in flight is a
          // no-op — this is what stops a double-click/tap from firing two
          // finalize requests for the same pay intent. React state alone
          // can't be trusted to reject the duplicate here (`clearState`
          // below resets `isInPaymentProcess` to `false` before setting it
          // back to `true`), so a synchronous ref is required.
          if (isRunPaymentInProgressRef.current) {
            logInfo(InfoLogs.checkoutPaymentDuplicateBlocked, {
              provider,
              payFromExpressCheckout,
            });
            return undefined;
          }
          try {
            // Set the guard AFTER `clearState`, which unconditionally
            // releases it (e.g. on payment-method change) — setting it
            // before would let that release race the attempt this call is
            // about to start.
            clearState();
            isRunPaymentInProgressRef.current = true;
            const selectedPaymentMethod = payFromExpressCheckout
              ? payFromExpressCheckout
              : checkout.paymentMethodSelected === PaymentMethodEnum.OTHER
                ? checkout.otherMethodSelected
                : checkout.paymentMethodSelected;
            // Labels (not only stringified data) so RUM can filter GPay vs card
            // without scraping `log_context.data`.
            logInfo(
              InfoLogs.checkoutPaymentStarted,
              {
                provider,
                payFromExpressCheckout,
              },
              {
                payment_method: selectedPaymentMethod ?? '',
                payment_provider: provider ?? '',
                pay_from_express: Boolean(payFromExpressCheckout),
              }
            );
            lastPaymentMethodUsedRef.current = selectedPaymentMethod as PaymentMethodEnum;
            if (payFromExpressCheckout) {
              payment.setIsExpressPaymentInProcess(true);
            } else {
              payment.setIsInPaymentProcess(true);
            }
            if (
              !payFromExpressCheckout &&
              provider &&
              isRequireToOpenTab(provider, selectedPaymentMethod as PaymentMethodEnum)
            ) {
              openRedirectTab();
            }

            if (!payFromExpressCheckout) {
              trackPaymentAttempt(context, params, selectedPaymentMethod as PaymentMethodEnum);
            }

            const paymentToServer = isAppchargeNewCardPayment(context, params)
              ? sendPciPaymentToServer
              : sendPaymentToServer;
            const results = (await paymentHandler.processPayment(
              selectedPaymentMethod as PaymentMethodEnum,
              context,
              paymentToServer,
              params
            )) as TResponse;

            return handlePaymentResults(
              results,
              selectedPaymentMethod as ExcludeOtherPaymentMethod,
              checkout.paymentKeys
            ) as TResponse;
          } catch (error: unknown) {
            logError(ErrorLogs.checkoutPaymentError, error, {
              paymentMethod: lastPaymentMethodUsedRef.current ?? 'none',
            });
            const isServerFinalizeFailure = checkIfServerFinalizeFailure(error as Error);
            if (isServerFinalizeFailure) {
              showBoundary('paymentProcessingFailed');
            } else {
              // A decline thrown as a non-2xx response carries the parsed
              // body on `ApiError.info`; lift its `appChargeErrorType` so
              // the shopper sees the real reason ("card declined", etc.).
              // Absent a specific type, fall back to decline copy instead
              // of the generic "Something went wrong".
              const info = (error as ApiError)?.info;
              const declineType =
                info && typeof info === 'object'
                  ? (info as { appChargeErrorType?: AppchargeErrorType }).appChargeErrorType
                  : undefined;
              setError(getAppchargeErrorMessage(t, declineType));
            }
            const attemptEventData = paymentAttemptEventDataRef.current;
            // Finalize senders rethrow as `new Error(failedToSendPayment,
            // { cause: apiError })`, so the gateway body (message + `errorCode`)
            // rides on `.cause`, not the wrapper. Read through the cause so a
            // thrown session failure (e.g. `SESSION_ENDED` on an expired
            // session) still forwards its reason and stable code to the
            // publisher instead of an empty error. `getApiErrorCode` already
            // hops the cause; mirror that for the reason text.
            const apiFailureReason =
              getRawApiErrorMessage((error as { cause?: unknown })?.cause) ?? getRawApiErrorMessage(error);
            notifyPaymentIntentFailed(checkoutData.data!, lastPaymentMethodUsedRef.current as PaymentMethodEnum, {
              reason: apiFailureReason,
              promoCode: attemptEventData?.promoCode,
              isSavedPaymentMethodUsed: attemptEventData?.isSavedPaymentMethodUsed,
              isNewPaymentMethodSaved: attemptEventData?.isNewPaymentMethodSaved,
              // A thrown finalize failure is terminal (this catch shows the
              // `paymentProcessingFailed` boundary), so mark it fatal.
              error: { reason: apiFailureReason, code: getApiErrorCode(error), isFatal: true },
            });
            payment.setIsInPaymentProcess(false);
            payment.setIsExpressPaymentInProcess(false);
            isRunPaymentInProgressRef.current = false;
            closeRedirectTab();
          }
        },
        sendPaymentToServer,
        updatePriceBreakdown: (priceBreakdown: PriceBreakdown) =>
          checkout.setPricing({ ...checkout.pricing!, priceBreakdown }),
        setSuccessScreen: (successScreen: SuccessScreenContext) => setSuccessScreen(successScreen),
        setError: (error: string | null) => setError(error),
        setOrderId: (orderId: string | undefined) => checkout.setOrderId(orderId),
        setSelectedCard: (card: SavedCard) => checkout.setSelectedCard(card),
        setTemplate: (template: Template) => checkout.setTemplate(template),
      },
      redirectPayment: redirectPayment ?? {},
      paymentAttempt: paymentAttemptEventDataRef.current,
      otherMethodSelected: checkout.otherMethodSelected,
      publisherId: bootData.boot?.publisherData.id || '',
      isLoading: false,
      template: checkout.template ?? getTemplateConfig(TemplateType.DEFAULT),
      totalPrice: formatTotalPrice(checkoutData.data!.pricing),
      purchasedItems: checkout.purchasedItems,
      customer: checkout.customer!,
      paymentMethodsSettings: checkout.paymentMethods,
      publisher: checkout.publisher!,
      pricing: checkout.pricing!,
      offer: checkout.offer,
      offers: checkout.offers,
      featureFlags: bootData.boot!.featureFlags,
      sessionStartedWithoutValidTax: checkout.sessionStartedWithoutValidTax,
      viewer: {
        screen: checkout.viewer,
        setViewer: (screen?: DrawerScreen) => checkout.setViewer(screen),
        closeViewer: () => checkout.setViewer(undefined),
      },
      addressDetails: checkout.addressDetails,
      // Prefer polling `orderData` (available the same render as PollingStatus.SUCCESS)
      // over checkout.orderData (only set in a later effect). Without this merge,
      // successScreen opens without publisherPurchaseId and redirect URLs omit purchaseId.
      successScreen: {
        ...(successScreen ?? { isOpen: false }),
        orderData: successScreen?.orderData ?? orderData ?? checkout.orderData,
      },
      payment: {
        isInPaymentProcess: payment.isInPaymentProcess,
        isExpressPaymentInProcess: payment.isExpressPaymentInProcess,
        isApplePayTabOpen: payment.isApplePayTabOpen,
        isFormValid: payment.isFormValid,
      },
      card: {
        savedCards: checkout.savedCards,
        savedPaypalAccount: checkout.savedPaypalAccount,
        clearSavedPaypalAccount: () => checkout.setSavedPaypalAccount(undefined),
        selectedCard: checkout.selectedCard,
        selectCard: (card) => checkout.setSelectedCard(card),
        saveNewCard: (card) => checkout.setSelectedCard({ ...card, isNew: true }),
        cardElementRef: cardElementRef,
        setCardElementRef: setCardElementRef,
        adyenCardDetailsRef: adyenCardDetailsRef,
        initialCardPayload:
          checkout?.initialCardPayload ??
          ({
            isSaveCard:
              Boolean(checkout.checkoutConfiguration?.showSaveMyCard) && (isDefaultSaveCardAllowedForLocation ?? false),
          } as InitialCardPayload),
        setInitialCardPayload: (initialCardPayload: InitialCardPayload) =>
          checkout.setInitialCardPayload(initialCardPayload),
      },
      paymentKeys: checkout.paymentKeys,
      drawer: {
        screen: checkout.drawerScreen,
        setDrawerScreen: (screen: DrawerScreen) => checkout.setDrawerScreen(screen),
        closeDrawer,
      },
      stripe: stripeInstance,
      checkoutConfiguration: checkout.checkoutConfiguration!,
      error,
      threeDSSecurityState: threeDSSecurityState,
    };
    return context;
  }, [
    checkout,
    checkoutData,
    bootData,
    closeDrawer,
    saveNewCard,
    payment,
    stripeInstance,
    cardElementRef,
    setCardElementRef,
    isApiInitilized,
    sendPaymentToServer,
    redirectPayment,
    successScreen,
    handlePaymentResults,
    error,
    threeDSSecurityState,
    adyenCardDetailsRef,
    clearState,
    closeCheckout,
    createSession,
    triggerOrderPolling,
    trackPaymentAttempt,
    resumeApplePayTabPayment,
    showMaintenance,
  ]);
  const isCheckoutReady = Boolean(checkoutContext && checkout.dataIsReady);
  const deferBootContentEnter = shouldDeferBootContentEnter(checkoutContext);

  useEffect(() => {
    if (statusOrderPolling === PollingStatus.SUCCESS) {
      payment.setIsInPaymentProcess(false);
      payment.setIsExpressPaymentInProcess(false);
      isRunPaymentInProgressRef.current = false;
      if (orderData) {
        checkout.setOrderData(orderData);
      }
      const attemptEventData = paymentAttemptEventDataRef.current;

      notifyOrderSuccess(
        getEventDataForSdkEvents(checkoutData.data!, {
          paymentMethodName: lastPaymentMethodUsedRef.current,
          promoCode: attemptEventData?.promoCode ?? null,
          isSavedPaymentMethodUsed: attemptEventData?.isSavedPaymentMethodUsed,
          isNewPaymentMethodSaved: attemptEventData?.isNewPaymentMethodSaved,
        })
      );

      // Must run here — not from `<SuccessScreen/>`'s mount effect. Child
      // effects run before parent effects on the same commit, so a close posted
      // from the success screen would beat `ORDER_COMPLETED_SUCCESS` and hosts
      // that unmount on `CLOSE_CHECKOUT` would miss fulfillment.
      if (
        checkoutContext &&
        shouldCloseCheckoutOnHiddenSuccessScreen(
          checkoutContext.featureFlags,
          resolveIsRedirectFlow(checkout.checkoutConfiguration)
        )
      ) {
        closeCheckout(checkoutContext);
      }
    }
    if (statusOrderPolling === PollingStatus.FAILED) {
      showBoundary('unableToCompleteOrder');
      const attemptEventData = paymentAttemptEventDataRef.current;
      // A terminal `charge_failed` carries no BE text to forward, so the event
      // ships without a reason. Only a thrown poll (network / non-2xx) has a
      // raw message worth attributing.
      const orderFailReason = orderData?.state === ChargeStates.FAILED ? undefined : orderPollingError;
      // A fatal failure must always carry a code. Forward the gateway's
      // `errorCode` when it sent one (e.g. `SESSION_ENDED` on a thrown poll),
      // otherwise stamp a client-originated `INTERNAL_ERROR`. A terminal
      // `charge_failed` (HTTP 200) exposes no gateway `errorCode`, so it too
      // falls back to `INTERNAL_ERROR` rather than shipping code-less.
      const orderFailCode = orderPollingErrorCode ?? CheckoutSessionErrorCode.INTERNAL_ERROR;

      notifyOrderFailed(
        getEventDataForSdkEvents(checkoutData.data!, {
          paymentMethodName: lastPaymentMethodUsedRef.current,
          promoCode: attemptEventData?.promoCode ?? null,
          isSavedPaymentMethodUsed: attemptEventData?.isSavedPaymentMethodUsed,
          isNewPaymentMethodSaved: attemptEventData?.isNewPaymentMethodSaved,
          reason: orderFailReason,
          error: { reason: orderFailReason, code: orderFailCode, isFatal: true },
        })
      );
    }
    // Intentionally keyed on `statusOrderPolling` alone: this effect emits
    // terminal SDK events, so it must fire exactly once per status transition.
    // `useOrderPolling` commits `data`/`error` in the same batch as `status`,
    // so the closure already sees them; widening the deps (e.g. with the
    // react-query `checkoutData.data` identity, which changes on any refetch)
    // would re-fire and duplicate the event.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusOrderPolling]);

  useEffect(() => {
    if (!isCheckoutReady) {
      setShowBootSkeletonExit(true);
      return;
    }

    if (deferBootContentEnter) {
      setShowBootSkeletonExit(false);
      return;
    }

    setShowBootSkeletonExit(true);
    const timer = window.setTimeout(() => setShowBootSkeletonExit(false), 220);
    return () => window.clearTimeout(timer);
  }, [isCheckoutReady, deferBootContentEnter]);

  useEffect(initilizeCheckoutData, [initilizeCheckoutData]);

  useEffect(() => {
    if (stripeInstance) return;

    const initializeStripe = async () => {
      const clientSecret = getStripePaymentKey(checkout.paymentKeys, checkout.paymentMethods);

      if (!clientSecret) return;

      const stripe = await loadStripe(clientSecret, STRIPE_CONFIG);

      if (stripe) {
        // Fill `stripeService.stripe` only if no per-form
        // `useStripeInitialize` hook has already done so for the active
        // method. Top-level uses `getStripePaymentKey` (the card sub-MID pk,
        // then `pks.stripe`) while per-form uses the *selected* method's
        // sub-MID pk. They still differ when the active method is not the
        // card, and when top-level won, paying with that method threw
        // `IntegrationError: Please use the same instance of Stripe …`
        // because the element was bound to the method instance but
        // `createPaymentMethod` ran against the boot instance.
        //
        // The per-form hook is the authoritative writer for the active
        // form (its pk matches its `<Elements>` provider exactly).
        // iDEAL, saved Link, card 3DS, and Cash App / PIX use
        // the instance their own form registered.
        // The `isStripeInitialized()` guard makes the two writers
        // commutative regardless of resolution order.
        if (!stripeService.isStripeInitialized()) {
          stripeService.initialize(stripe);
        }
        setStripeInstance(stripe);
      }
    };

    initializeStripe();
  }, [checkout.paymentKeys, checkout.paymentMethods, stripeInstance]);

  if (!isCheckoutReady) {
    return (
      <div className="checkout-boot-stage checkout-boot-stage--loading">
        <CheckoutSkeletonLoader />
      </div>
    );
  }

  return (
    <CheckoutContext.Provider value={checkoutContext!}>
      <div className="checkout-boot-stage checkout-boot-stage--ready">
        <div className={deferBootContentEnter ? 'checkout-boot-content-hold' : 'checkout-boot-content-enter'}>
          <CheckoutWrapper statusOrderPolling={statusOrderPolling} />
        </div>
        <StripeApmNextActionForm state={stripeApmAction} fallbackStripe={stripeInstance} />
        {showBootSkeletonExit && !deferBootContentEnter && (
          <div className="checkout-boot-skeleton-exit" aria-hidden="true">
            <CheckoutSkeletonLoader />
          </div>
        )}
      </div>
    </CheckoutContext.Provider>
  );
};

const CheckoutWrapper = ({ statusOrderPolling }: { statusOrderPolling: PollingStatus }) => {
  const context = useContext(CheckoutContext);
  const { showMaintenance } = context;
  useApplicationState();

  if (showMaintenance) {
    return <Maintenance />;
  }

  if (shouldRenderPaymentHandlerPage()) {
    return <StripePaymentHandlerPage />;
  }

  return <TemplateRenderer statusOrderPolling={statusOrderPolling} />;
};
